import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Progress,
  Avatar,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Grid,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tag,
  Divider,
} from '@chakra-ui/react';
import {
  FaGlobe,
  FaChartBar,
  FaTrophy,
  FaMedal,
  FaAward,
  FaDollarSign,
  FaSearch,
  FaAd,
  FaBalanceScale,
  FaChartPie,
  FaExchangeAlt,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import BusinessReviewModal from '../BusinessReviewModal';
import { getBusinessImages } from '../../services/imageService';
import { getYearData } from '../../services/yearService';
import {
  getDomainOverview,
  getDomainOrganicKeywords,
  getDomainPaidKeywords,
} from '../../services/domainIntelligenceService';
import { logger } from '../../utils/logger';
import {
  hasCompetitiveIntel,
  getDomainStrength,
  getMonthlyOrganicClicks,
  getMonthlyOrganicValue,
  getMonthlyPaidClicks,
  getTotalPaidKeywords,
  getTotalOrganicKeywords,
  getMonthlyPPCBudget,
  getAverageOrganicRank,
  formatNumber as formatNum,
  formatCurrency,
  getTrafficValue,
} from '../../utils/competitiveIntelHelpers';

const COLORS = {
  organic: '#10B981',
  paid: '#F59E0B',
  organicLight: '#D1FAE5',
  paidLight: '#FEF3C7',
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',
  chart: [
    '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
    '#06B6D4', '#EC4899', '#14B8A6', '#6366F1', '#F97316',
  ],
};

const OrganicPaidTrafficDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [businessImages, setBusinessImages] = useState({});
  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetail, setDomainDetail] = useState(null);
  const [isDomainLoading, setIsDomainLoading] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const mutedColor = useColorModeValue('gray.500', 'gray.500');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const glassBg = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(26,32,44,0.95)');
  const tabContainerBg = useColorModeValue('gray.100', 'gray.700');
  const tabColor = useColorModeValue('gray.600', 'gray.400');
  const tabSelectedBg = useColorModeValue('white', 'gray.600');
  const tabSelectedColor = useColorModeValue('green.600', 'green.200');
  const tabHoverColor = useColorModeValue('green.500', 'green.300');

  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      return;
    }
    const fetchYearData = async () => {
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);
        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );
          return { ...currentData, historicalData: historicalData?.data || null };
        });
        setCombinedData(combined);
      } catch (error) {
        logger.error('Error fetching year data:', error);
      }
    };
    if (reviewsData.length > 0) {
      fetchYearData();
    }
  }, [reviewsData]);

  const handleBusinessClick = useCallback((business) => {
    const businessWithData = combinedData.find(
      (b) => b.maps_uri === business.maps_uri
    );
    setSelectedBusiness(businessWithData || business);
    setIsBusinessModalOpen(true);
  }, [combinedData]);

  const closeBusinessModal = () => {
    setIsBusinessModalOpen(false);
    setSelectedBusiness(null);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const businessesWithData = useMemo(() => {
    return reviewsData.filter(business => hasCompetitiveIntel(business));
  }, [reviewsData]);

  const enrichedBusinesses = useMemo(() => {
    return businessesWithData.map(business => ({
      ...business,
      organicClicks: getMonthlyOrganicClicks(business),
      paidClicks: getMonthlyPaidClicks(business),
      organicValue: getMonthlyOrganicValue(business),
      ppcBudget: getMonthlyPPCBudget(business),
      organicKeywords: getTotalOrganicKeywords(business),
      paidKeywords: getTotalPaidKeywords(business),
      domainStrength: getDomainStrength(business),
      avgOrganicRank: getAverageOrganicRank(business),
      totalTraffic: getMonthlyOrganicClicks(business) + getMonthlyPaidClicks(business),
      organicShare: (() => {
        const org = getMonthlyOrganicClicks(business);
        const paid = getMonthlyPaidClicks(business);
        const total = org + paid;
        return total > 0 ? Math.round((org / total) * 100) : 0;
      })(),
    })).sort((a, b) => b.totalTraffic - a.totalTraffic);
  }, [businessesWithData]);

  const marketStats = useMemo(() => {
    if (!enrichedBusinesses.length) return null;

    const totalOrganicClicks = enrichedBusinesses.reduce((sum, b) => sum + b.organicClicks, 0);
    const totalPaidClicks = enrichedBusinesses.reduce((sum, b) => sum + b.paidClicks, 0);
    const totalOrganicValue = enrichedBusinesses.reduce((sum, b) => sum + b.organicValue, 0);
    const totalPPCBudget = enrichedBusinesses.reduce((sum, b) => sum + b.ppcBudget, 0);
    const totalOrganicKeywords = enrichedBusinesses.reduce((sum, b) => sum + b.organicKeywords, 0);
    const totalPaidKeywords = enrichedBusinesses.reduce((sum, b) => sum + b.paidKeywords, 0);
    const totalAllTraffic = totalOrganicClicks + totalPaidClicks;
    const marketOrganicShare = totalAllTraffic > 0 ? Math.round((totalOrganicClicks / totalAllTraffic) * 100) : 0;

    return {
      totalOrganicClicks,
      totalPaidClicks,
      totalOrganicValue,
      totalPPCBudget,
      totalOrganicKeywords,
      totalPaidKeywords,
      totalAllTraffic,
      marketOrganicShare,
      businessCount: enrichedBusinesses.length,
    };
  }, [enrichedBusinesses]);

  const trafficSplitChartData = useMemo(() => {
    if (!marketStats) return [];
    return [
      { name: 'Organic', value: marketStats.totalOrganicClicks, color: COLORS.organic },
      { name: 'Paid', value: marketStats.totalPaidClicks, color: COLORS.paid },
    ].filter(d => d.value > 0);
  }, [marketStats]);

  const businessComparisonData = useMemo(() => {
    return enrichedBusinesses.slice(0, 12).map(b => ({
      name: b.name?.split(' ').slice(0, 2).join(' ') || 'Unknown',
      organic: b.organicClicks,
      paid: b.paidClicks,
      total: b.totalTraffic,
    }));
  }, [enrichedBusinesses]);

  const valueSplitData = useMemo(() => {
    if (!marketStats) return [];
    return [
      { name: 'Organic Value', value: marketStats.totalOrganicValue, color: COLORS.organic },
      { name: 'PPC Budget', value: marketStats.totalPPCBudget, color: COLORS.paid },
    ].filter(d => d.value > 0);
  }, [marketStats]);

  const keywordSplitData = useMemo(() => {
    if (!marketStats) return [];
    return [
      { name: 'Organic Keywords', value: marketStats.totalOrganicKeywords, color: COLORS.organic },
      { name: 'Paid Keywords', value: marketStats.totalPaidKeywords, color: COLORS.paid },
    ].filter(d => d.value > 0);
  }, [marketStats]);

  const loadDomainDetail = async (business) => {
    const domain = business.spyfu_data?.domain;
    if (!domain) return;
    setIsDomainLoading(true);
    setSelectedDomain(domain);
    try {
      const [overviewRes, organicRes, paidRes] = await Promise.all([
        getDomainOverview(domain).catch(() => null),
        getDomainOrganicKeywords(domain, 'US', 1, 50).catch(() => null),
        getDomainPaidKeywords(domain, 'US', 1, 50).catch(() => null),
      ]);
      setDomainDetail({
        overview: overviewRes?.data || null,
        organicKeywords: organicRes?.data?.results || [],
        paidKeywords: paidRes?.data?.results || [],
        business,
      });
    } catch (error) {
      logger.error('Error loading domain detail:', error);
    } finally {
      setIsDomainLoading(false);
    }
  };

  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const sb = business.historicalData?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total = sb.positive + sb.neutral + sb.negative;
      return { ...business, sentimentScore: total > 0 ? (sb.positive / total) * 100 : 0 };
    }).sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      const recentMonths = months.slice(-3);
      const totalGrowth = recentMonths.reduce((sum, month) => sum + (monthlyData[month] || 0), 0);
      return { ...business, totalGrowth };
    }).sort((a, b) => b.totalGrowth - a.totalGrowth);
  }, [combinedData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => ({ ...business, trafficValue: getTrafficValue(business) || 0 }))
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Compare organic search traffic versus paid advertising performance across businesses in your market.">
          Organic vs Paid Traffic
        </SectionLabel>}
        <Flex direction="column" align="center" justify="center" minH="400px" p={8}>
          <Icon as={FaBalanceScale} boxSize={16} color="gray.300" mb={4} />
          <Heading size="md" color="gray.500" mb={2}>No Traffic Data Available</Heading>
          <Text color="gray.400" textAlign="center">
            Organic vs paid traffic analytics will appear here once data is available for your market.
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!businessesWithData.length) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Compare organic search traffic versus paid advertising performance across businesses in your market.">
          Organic vs Paid Traffic
        </SectionLabel>}
        <Alert status="info" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>No Traffic Intelligence Yet</AlertTitle>
            <AlertDescription>
              SpyFu traffic data is being collected for businesses in your market. Check back soon!
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  const top3 = enrichedBusinesses.slice(0, 3);

  return (
    <Box>
      {/* Top 3 Traffic Leaders Podium */}
      {top3.length >= 3 && (
        <Box display={{ base: 'none', md: 'block' }} mb={8}>
          <Flex justify="center" align="flex-end" gap={6} w="full">
            {/* Silver */}
            <Box
              bg={cardBg} borderRadius="2xl" textAlign="center" position="relative" cursor="pointer"
              onClick={() => handleBusinessClick(top3[1])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-8px)', shadow: '2xl' }}
              overflow="hidden" flex="1" maxW="320px" boxShadow="xl"
              borderWidth="1px" borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={5} spacing={3}>
                <Flex position="absolute" top={3} left={3} w="28px" h="28px" borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)" justify="center" align="center" shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">2</Text>
                </Flex>
                <Icon as={FaMedal} color="gray.400" boxSize={5} />
                <Avatar name={top3[1]?.name} src={businessImages[top3[1]?.maps_uri] || ''} size="md" />
                <VStack spacing={0}>
                  <Tooltip label={top3[1]?.name} placement="top" hasArrow>
                    <Heading size="sm" noOfLines={1}>{top3[1]?.name}</Heading>
                  </Tooltip>
                  <Text fontWeight="extrabold" fontSize="2xl" color="blue.600">
                    {formatNumber(top3[1]?.totalTraffic)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">total clicks/mo</Text>
                </VStack>
                <HStack spacing={3} fontSize="xs" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="green.500">{formatNumber(top3[1]?.organicClicks)}</Text>
                    <Text>organic</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="yellow.600">{formatNumber(top3[1]?.paidClicks)}</Text>
                    <Text>paid</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Gold */}
            <Box
              bg={cardBg} borderRadius="2xl" textAlign="center" position="relative" cursor="pointer"
              onClick={() => handleBusinessClick(top3[0])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-12px) scale(1.02)', shadow: '0 25px 50px -12px rgba(16, 185, 129, 0.3)' }}
              overflow="hidden" flex="1.15" maxW="380px"
              boxShadow="0 20px 40px -12px rgba(16, 185, 129, 0.25)"
              zIndex={2} mb={4} borderWidth="1px" borderColor="green.200"
            >
              <Box
                h="6px" bgGradient="linear(to-r, green.400, yellow.400, green.400)"
                backgroundSize="200% 100%" animation="shimmer 2s linear infinite"
                sx={{ '@keyframes shimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } } }}
              />
              <VStack p={6} spacing={4}>
                <Flex position="absolute" top={3} left={3} w="32px" h="32px" borderRadius="full"
                  bgGradient="linear(to-br, green.400, yellow.500)" justify="center" align="center" shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="md">1</Text>
                </Flex>
                <Icon as={FaTrophy} color="green.400" boxSize={8} filter="drop-shadow(0 2px 4px rgba(16, 185, 129, 0.5))" />
                <Avatar name={top3[0]?.name} src={businessImages[top3[0]?.maps_uri] || ''} size="lg" borderWidth="3px" borderColor="green.200" />
                <VStack spacing={0}>
                  <Tooltip label={top3[0]?.name} placement="top" hasArrow>
                    <Heading size="md" noOfLines={1}>{top3[0]?.name}</Heading>
                  </Tooltip>
                  <Text fontWeight="extrabold" fontSize="3xl" bgGradient="linear(to-r, green.500, yellow.500)" bgClip="text">
                    {formatNumber(top3[0]?.totalTraffic)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">total clicks/mo</Text>
                </VStack>
                <HStack spacing={4} fontSize="sm" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="green.500">{formatNumber(top3[0]?.organicClicks)}</Text>
                    <Text fontSize="xs">organic</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="yellow.600">{formatNumber(top3[0]?.paidClicks)}</Text>
                    <Text fontSize="xs">paid</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="blue.500">{top3[0]?.organicShare}%</Text>
                    <Text fontSize="xs">organic share</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Bronze */}
            <Box
              bg={cardBg} borderRadius="2xl" textAlign="center" position="relative" cursor="pointer"
              onClick={() => handleBusinessClick(top3[2])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-8px)', shadow: '2xl' }}
              overflow="hidden" flex="1" maxW="320px" boxShadow="xl"
              borderWidth="1px" borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={5} spacing={3}>
                <Flex position="absolute" top={3} left={3} w="28px" h="28px" borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)" justify="center" align="center" shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">3</Text>
                </Flex>
                <Icon as={FaAward} color="orange.400" boxSize={5} />
                <Avatar name={top3[2]?.name} src={businessImages[top3[2]?.maps_uri] || ''} size="md" />
                <VStack spacing={0}>
                  <Tooltip label={top3[2]?.name} placement="top" hasArrow>
                    <Heading size="sm" noOfLines={1}>{top3[2]?.name}</Heading>
                  </Tooltip>
                  <Text fontWeight="extrabold" fontSize="2xl" color="orange.500">
                    {formatNumber(top3[2]?.totalTraffic)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">total clicks/mo</Text>
                </VStack>
                <HStack spacing={3} fontSize="xs" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="green.500">{formatNumber(top3[2]?.organicClicks)}</Text>
                    <Text>organic</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="yellow.600">{formatNumber(top3[2]?.paidClicks)}</Text>
                    <Text>paid</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {!demoMode && <SectionLabel description="Compare organic search traffic versus paid advertising performance across businesses in your market.">
        Organic vs Paid Traffic
      </SectionLabel>}

      {/* Market Overview Stats */}
      {marketStats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>
                  <HStack spacing={1}><Icon as={FaSearch} boxSize={3} color="green.500" /><Text>Organic Clicks</Text></HStack>
                </StatLabel>
                <StatNumber color="green.500">{formatNum(marketStats.totalOrganicClicks)}</StatNumber>
                <StatHelpText>{formatNum(marketStats.totalOrganicKeywords)} keywords</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>
                  <HStack spacing={1}><Icon as={FaAd} boxSize={3} color="yellow.500" /><Text>Paid Clicks</Text></HStack>
                </StatLabel>
                <StatNumber color="yellow.600">{formatNum(marketStats.totalPaidClicks)}</StatNumber>
                <StatHelpText>{formatNum(marketStats.totalPaidKeywords)} paid keywords</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>
                  <HStack spacing={1}><Icon as={FaDollarSign} boxSize={3} color="green.500" /><Text>Organic Value</Text></HStack>
                </StatLabel>
                <StatNumber color="green.500">{formatCurrency(marketStats.totalOrganicValue)}</StatNumber>
                <StatHelpText>Est. monthly value</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>
                  <HStack spacing={1}><Icon as={FaDollarSign} boxSize={3} color="yellow.500" /><Text>PPC Budget</Text></HStack>
                </StatLabel>
                <StatNumber color="yellow.600">{formatCurrency(marketStats.totalPPCBudget)}</StatNumber>
                <StatHelpText>Est. monthly spend</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Main Tabs */}
      <Tabs variant="unstyled" mb={8} index={activeTab} onChange={setActiveTab}>
        <TabList
          mb={6}
          display="grid"
          gridTemplateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap={3}
          bg={tabContainerBg}
          p={1.5}
          borderRadius="xl"
        >
          {[
            { icon: FaChartPie, label: 'Traffic Split' },
            { icon: FaChartBar, label: 'Business Comparison' },
            { icon: FaExchangeAlt, label: 'All Businesses' },
            { icon: FaGlobe, label: 'Domain Deep Dive' },
          ].map(({ icon, label }) => (
            <Tab
              key={label}
              py={3}
              px={4}
              borderRadius="lg"
              fontWeight="600"
              fontSize="sm"
              color={tabColor}
              transition="all 0.2s ease"
              _selected={{
                bg: tabSelectedBg,
                color: tabSelectedColor,
                shadow: 'md',
                transform: 'translateY(-1px)',
              }}
              _hover={{
                color: tabHoverColor,
              }}
            >
              <HStack spacing={2} justify="center">
                <Icon as={icon} boxSize={4} />
                <Text>{label}</Text>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {/* Traffic Split Panel */}
          <TabPanel p={0}>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
              {/* Traffic Split Pie */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={4}>
                  <Heading size="md">Market Traffic Split</Heading>
                  <Text fontSize="sm" color={subtitleColor}>
                    Organic vs paid clicks across {marketStats?.businessCount} businesses
                  </Text>
                </VStack>
                <Box h="320px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={trafficSplitChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={4}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                      >
                        {trafficSplitChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                                <Text fontWeight="bold">{payload[0].name}</Text>
                                <Text>{formatNum(payload[0].value)} clicks/mo</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                {marketStats && (
                  <HStack justify="center" spacing={6} mt={2}>
                    <HStack>
                      <Box w={3} h={3} borderRadius="full" bg={COLORS.organic} />
                      <Text fontSize="sm" color={subtitleColor}>
                        Organic: {marketStats.marketOrganicShare}%
                      </Text>
                    </HStack>
                    <HStack>
                      <Box w={3} h={3} borderRadius="full" bg={COLORS.paid} />
                      <Text fontSize="sm" color={subtitleColor}>
                        Paid: {100 - marketStats.marketOrganicShare}%
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </Box>

              {/* Value Split */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={4}>
                  <Heading size="md">Traffic Value Breakdown</Heading>
                  <Text fontSize="sm" color={subtitleColor}>
                    Organic SEO value vs PPC ad spend
                  </Text>
                </VStack>
                <Box h="320px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={valueSplitData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={4}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                      >
                        {valueSplitData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                                <Text fontWeight="bold">{payload[0].name}</Text>
                                <Text>{formatCurrency(payload[0].value)}/mo</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                {marketStats && (
                  <HStack justify="center" spacing={6} mt={2}>
                    <HStack>
                      <Box w={3} h={3} borderRadius="full" bg={COLORS.organic} />
                      <Text fontSize="sm" color={subtitleColor}>
                        Organic: {formatCurrency(marketStats.totalOrganicValue)}
                      </Text>
                    </HStack>
                    <HStack>
                      <Box w={3} h={3} borderRadius="full" bg={COLORS.paid} />
                      <Text fontSize="sm" color={subtitleColor}>
                        PPC: {formatCurrency(marketStats.totalPPCBudget)}
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </Box>

              {/* Keyword Split */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={4}>
                  <Heading size="md">Keyword Distribution</Heading>
                  <Text fontSize="sm" color={subtitleColor}>
                    Organic vs paid keyword counts across all businesses
                  </Text>
                </VStack>
                <Box h="320px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={keywordSplitData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={4}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                      >
                        {keywordSplitData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                                <Text fontWeight="bold">{payload[0].name}</Text>
                                <Text>{formatNum(payload[0].value)} keywords</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Traffic Strategy Quadrant */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <VStack align="start" spacing={1} mb={4}>
                  <Heading size="md">Traffic Strategy Matrix</Heading>
                  <Text fontSize="sm" color={subtitleColor}>
                    How businesses balance organic vs paid traffic
                  </Text>
                </VStack>
                <VStack spacing={3} align="stretch" maxH="320px" overflowY="auto">
                  {enrichedBusinesses.slice(0, 10).map((business, index) => {
                    const strategy = business.organicShare >= 80 ? 'SEO-Heavy'
                      : business.organicShare >= 50 ? 'Balanced'
                      : business.organicShare > 0 ? 'PPC-Heavy'
                      : 'Organic Only';
                    const strategyColor = strategy === 'SEO-Heavy' ? 'green'
                      : strategy === 'Balanced' ? 'blue'
                      : strategy === 'PPC-Heavy' ? 'orange'
                      : 'teal';

                    return (
                      <HStack
                        key={index}
                        p={3}
                        bg={statBg}
                        borderRadius="lg"
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleBusinessClick(business)}
                        transition="all 0.2s"
                      >
                        <Avatar size="sm" name={business.name} src={businessImages[business.maps_uri]} />
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="medium" fontSize="sm" noOfLines={1}>{business.name}</Text>
                          <HStack spacing={1}>
                            <Badge colorScheme={strategyColor} fontSize="xs">{strategy}</Badge>
                            <Text fontSize="xs" color={mutedColor}>{business.organicShare}% organic</Text>
                          </HStack>
                        </VStack>
                        <VStack spacing={0} align="end">
                          <Text fontWeight="bold" fontSize="sm">{formatNumber(business.totalTraffic)}</Text>
                          <Text fontSize="xs" color={mutedColor}>clicks/mo</Text>
                        </VStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            </Grid>
          </TabPanel>

          {/* Business Comparison Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6} mb={6}>
              <VStack align="start" spacing={1} mb={4}>
                <Heading size="md">Organic vs Paid Traffic by Business</Heading>
                <Text fontSize="sm" color={subtitleColor}>
                  Stacked bar chart showing traffic composition per business
                </Text>
              </VStack>
              <Box h="450px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={businessComparisonData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={95} />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const organic = payload.find(p => p.dataKey === 'organic')?.value || 0;
                          const paid = payload.find(p => p.dataKey === 'paid')?.value || 0;
                          const total = organic + paid;
                          return (
                            <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                              <Text fontWeight="bold" mb={1}>{label}</Text>
                              <HStack justify="space-between"><Text color="green.500">Organic:</Text><Text fontWeight="bold">{formatNum(organic)}</Text></HStack>
                              <HStack justify="space-between"><Text color="yellow.600">Paid:</Text><Text fontWeight="bold">{formatNum(paid)}</Text></HStack>
                              <Divider my={1} />
                              <HStack justify="space-between"><Text fontWeight="bold">Total:</Text><Text fontWeight="bold">{formatNum(total)}</Text></HStack>
                              {total > 0 && <Text fontSize="xs" color={mutedColor}>{Math.round((organic / total) * 100)}% organic</Text>}
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="organic" stackId="traffic" fill={COLORS.organic} name="Organic" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="paid" stackId="traffic" fill={COLORS.paid} name="Paid" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* Organic Share Ranking */}
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <VStack align="start" spacing={1} mb={4}>
                <Heading size="md">Organic Traffic Share Ranking</Heading>
                <Text fontSize="sm" color={subtitleColor}>
                  Businesses ranked by percentage of traffic from organic search
                </Text>
              </VStack>
              <Box h="350px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={enrichedBusinesses
                      .filter(b => b.totalTraffic > 0)
                      .sort((a, b) => b.organicShare - a.organicShare)
                      .slice(0, 12)
                      .map(b => ({
                        name: b.name?.split(' ').slice(0, 2).join(' ') || 'Unknown',
                        organicShare: b.organicShare,
                        paidShare: 100 - b.organicShare,
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={95} />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
                              <Text fontWeight="bold" mb={1}>{label}</Text>
                              <Text color="green.500">Organic: {payload[0]?.value}%</Text>
                              <Text color="yellow.600">Paid: {payload[1]?.value}%</Text>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="organicShare" stackId="share" fill={COLORS.organic} name="Organic %" />
                    <Bar dataKey="paidShare" stackId="share" fill={COLORS.paid} name="Paid %" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </TabPanel>

          {/* All Businesses Table */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflow="hidden">
              <Box p={4} borderBottomWidth="1px" borderColor={borderColor}>
                <Heading size="md">All Businesses - Organic vs Paid Metrics</Heading>
                <Text fontSize="sm" color={subtitleColor} mt={1}>
                  Complete breakdown of traffic, keywords, value, and spending for each business
                </Text>
              </Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={statBg}>
                    <Tr>
                      <Th>Business</Th>
                      <Th isNumeric>Organic Clicks</Th>
                      <Th isNumeric>Paid Clicks</Th>
                      <Th isNumeric>Total Traffic</Th>
                      <Th isNumeric>Organic %</Th>
                      <Th isNumeric>Organic Value</Th>
                      <Th isNumeric>PPC Budget</Th>
                      <Th isNumeric>Organic KW</Th>
                      <Th isNumeric>Paid KW</Th>
                      <Th isNumeric>Strength</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {enrichedBusinesses.map((business, index) => (
                      <Tr
                        key={index}
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleBusinessClick(business)}
                      >
                        <Td>
                          <HStack spacing={3}>
                            <Avatar size="sm" name={business.name} src={businessImages[business.maps_uri]} />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium" fontSize="sm" noOfLines={1}>{business.name}</Text>
                              <Text fontSize="xs" color={subtitleColor}>{business.spyfu_data?.domain}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium" color="green.500">{formatNum(business.organicClicks)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium" color="yellow.600">{formatNum(business.paidClicks)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="blue">{formatNum(business.totalTraffic)}</Badge>
                        </Td>
                        <Td isNumeric>
                          <HStack spacing={2} justify="flex-end">
                            <Progress
                              value={business.organicShare}
                              max={100}
                              colorScheme={business.organicShare >= 70 ? 'green' : business.organicShare >= 40 ? 'yellow' : 'orange'}
                              size="sm"
                              w="60px"
                              borderRadius="full"
                            />
                            <Text fontSize="xs" color={subtitleColor} minW="35px" textAlign="right">
                              {business.organicShare}%
                            </Text>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium" color="green.500">{formatCurrency(business.organicValue)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium" color="yellow.600">{formatCurrency(business.ppcBudget)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="green" variant="subtle">{formatNum(business.organicKeywords)}</Badge>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="orange" variant="subtle">{formatNum(business.paidKeywords)}</Badge>
                        </Td>
                        <Td isNumeric>
                          <VStack spacing={0}>
                            <Progress
                              value={business.domainStrength}
                              max={100}
                              colorScheme={business.domainStrength >= 50 ? 'green' : business.domainStrength >= 30 ? 'yellow' : 'red'}
                              size="sm"
                              w="60px"
                              borderRadius="full"
                            />
                            <Text fontSize="xs" color={subtitleColor}>{business.domainStrength}/100</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadDomainDetail(business);
                              setActiveTab(3);
                            }}
                          >
                            Deep Dive
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Domain Deep Dive Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <Heading size="md" mb={2}>
                <Icon as={FaGlobe} mr={2} color="green.500" />
                Domain Deep Dive
              </Heading>
              <Text color={subtitleColor} mb={6}>
                Select a business to see detailed organic vs paid keyword analysis.
              </Text>

              <HStack mb={6} flexWrap="wrap" gap={2}>
                {enrichedBusinesses.slice(0, 12).map((business, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={selectedDomain === business.spyfu_data?.domain ? 'solid' : 'outline'}
                    colorScheme="green"
                    onClick={() => loadDomainDetail(business)}
                    isLoading={isDomainLoading && selectedDomain === business.spyfu_data?.domain}
                  >
                    {business.name?.split(' ').slice(0, 2).join(' ')}
                  </Button>
                ))}
              </HStack>

              {isDomainLoading && (
                <Flex justify="center" py={12}>
                  <Spinner size="xl" color="green.500" />
                </Flex>
              )}

              {domainDetail && !isDomainLoading && (
                <Box>
                  {/* Domain Summary Cards */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                    <Card bg="green.50" borderWidth="1px" borderColor="green.200">
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color="green.700">Organic Clicks</StatLabel>
                          <StatNumber color="green.600" fontSize="xl">
                            {formatNum(getMonthlyOrganicClicks(domainDetail.business))}
                          </StatNumber>
                          <StatHelpText fontSize="xs">/month</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg="yellow.50" borderWidth="1px" borderColor="yellow.200">
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color="yellow.700">Paid Clicks</StatLabel>
                          <StatNumber color="yellow.600" fontSize="xl">
                            {formatNum(getMonthlyPaidClicks(domainDetail.business))}
                          </StatNumber>
                          <StatHelpText fontSize="xs">/month</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg="green.50" borderWidth="1px" borderColor="green.200">
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color="green.700">Organic Value</StatLabel>
                          <StatNumber color="green.600" fontSize="xl">
                            {formatCurrency(getMonthlyOrganicValue(domainDetail.business))}
                          </StatNumber>
                          <StatHelpText fontSize="xs">/month</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg="yellow.50" borderWidth="1px" borderColor="yellow.200">
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color="yellow.700">PPC Budget</StatLabel>
                          <StatNumber color="yellow.600" fontSize="xl">
                            {formatCurrency(getMonthlyPPCBudget(domainDetail.business))}
                          </StatNumber>
                          <StatHelpText fontSize="xs">/month</StatHelpText>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                    <Card bg={statBg} borderWidth="1px" borderColor={borderColor}>
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color={subtitleColor}>Organic Keywords</StatLabel>
                          <StatNumber fontSize="xl" color="green.500">
                            {formatNum(getTotalOrganicKeywords(domainDetail.business))}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg={statBg} borderWidth="1px" borderColor={borderColor}>
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color={subtitleColor}>Paid Keywords</StatLabel>
                          <StatNumber fontSize="xl" color="yellow.600">
                            {formatNum(getTotalPaidKeywords(domainDetail.business))}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg={statBg} borderWidth="1px" borderColor={borderColor}>
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color={subtitleColor}>Domain Strength</StatLabel>
                          <StatNumber fontSize="xl" color="blue.500">
                            {getDomainStrength(domainDetail.business)}/100
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card bg={statBg} borderWidth="1px" borderColor={borderColor}>
                      <CardBody py={3}>
                        <Stat size="sm">
                          <StatLabel color={subtitleColor}>Avg Organic Rank</StatLabel>
                          <StatNumber fontSize="xl" color="purple.500">
                            #{Math.round(getAverageOrganicRank(domainDetail.business))}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Keyword Lists Side by Side */}
                  <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                    <Box>
                      <HStack mb={4}>
                        <Icon as={FaSearch} color="green.500" />
                        <Heading size="sm">Top Organic Keywords</Heading>
                        <Badge colorScheme="green">{domainDetail.organicKeywords?.length || 0}</Badge>
                      </HStack>

                      {domainDetail.organicKeywords?.length > 0 ? (
                        <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                          {domainDetail.organicKeywords.slice(0, 25).map((kw, index) => (
                            <HStack key={index} p={3} bg={statBg} borderRadius="md" fontSize="sm">
                              <Badge colorScheme="green" minW="35px" textAlign="center">
                                #{kw.rank || kw.rankingPosition || '-'}
                              </Badge>
                              <Text flex={1} fontWeight="medium" noOfLines={1}>
                                {kw.keyword || kw.term}
                              </Text>
                              <Tag size="sm" colorScheme="blue">
                                {formatNum(kw.searchVolume || kw.exactLocalMonthlySearchVolume || 0)}
                              </Tag>
                            </HStack>
                          ))}
                        </VStack>
                      ) : (
                        <Text color={subtitleColor} fontSize="sm">No organic keyword data available</Text>
                      )}
                    </Box>

                    <Box>
                      <HStack mb={4}>
                        <Icon as={FaDollarSign} color="yellow.500" />
                        <Heading size="sm">Top Paid Keywords</Heading>
                        <Badge colorScheme="orange">{domainDetail.paidKeywords?.length || 0}</Badge>
                      </HStack>

                      {domainDetail.paidKeywords?.length > 0 ? (
                        <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                          {domainDetail.paidKeywords.slice(0, 25).map((kw, index) => (
                            <HStack key={index} p={3} bg={statBg} borderRadius="md" fontSize="sm">
                              <Badge colorScheme="orange" minW="35px" textAlign="center">
                                #{kw.rank || kw.rankingPosition || '-'}
                              </Badge>
                              <Text flex={1} fontWeight="medium" noOfLines={1}>
                                {kw.keyword || kw.term}
                              </Text>
                              <Tag size="sm" colorScheme="green">
                                {formatCurrency(kw.cpc || kw.costPerClick || 0)}
                              </Tag>
                            </HStack>
                          ))}
                        </VStack>
                      ) : (
                        <Text color={subtitleColor} fontSize="sm">No paid keyword data available</Text>
                      )}
                    </Box>
                  </Grid>
                </Box>
              )}

              {!selectedDomain && !isDomainLoading && (
                <Flex direction="column" align="center" py={12}>
                  <Icon as={FaGlobe} color="gray.300" boxSize={12} mb={4} />
                  <Text color={subtitleColor}>Select a business above to see their organic vs paid breakdown</Text>
                </Flex>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <BusinessReviewModal
        isOpen={isBusinessModalOpen}
        onClose={closeBusinessModal}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default OrganicPaidTrafficDashboard;
